/**
 * useAudioSession Hook
 * 
 * iOS-only hook for handling audio interruptions and route changes.
 * Uses @capgo/capacitor-audio-session plugin.
 * 
 * Features:
 * - Pause playback on phone call / Siri / other interruptions
 * - Resume playback after interruption ends (if appropriate)
 * - Handle headphone disconnect (pause playback)
 * - Handle Bluetooth device connect/disconnect
 */

import { useEffect, useRef, useCallback } from "react";
import { Capacitor } from "@capacitor/core";

// Route change reason values from the plugin (kebab-case)
type RouteChangeReason = 
	| "new-device-available"
	| "old-device-unavailable"
	| "category-change"
	| "override"
	| "wake-from-sleep"
	| "no-suitable-route-for-category"
	| "route-config-change"
	| "unknown";

// Interruption type values from the plugin
type InterruptionType = "began" | "ended";

// Callbacks for the hook consumer
export interface AudioSessionCallbacks {
	/** Called when an interruption begins (e.g., phone call) */
	onInterruptionBegan?: () => void;
	/** Called when an interruption ends */
	onInterruptionEnded?: () => void;
	/** Called when headphones are disconnected */
	onHeadphonesDisconnected?: () => void;
	/** Called when a new audio device is connected */
	onNewDeviceConnected?: () => void;
	/** Called on any route change */
	onRouteChange?: (reason: RouteChangeReason) => void;
}

export interface UseAudioSessionReturn {
	/** Whether we're on iOS (hook is active) */
	isActive: boolean;
	/** Manually set the audio session active state */
	setActive: (active: boolean) => Promise<void>;
}

/**
 * Hook for handling iOS audio session events.
 * On non-iOS platforms, this hook does nothing.
 * 
 * @param callbacks - Event callbacks
 * @param enabled - Whether to enable the listeners (useful for disabling when player is inactive)
 */
export function useAudioSession(
	callbacks: AudioSessionCallbacks,
	enabled: boolean = true
): UseAudioSessionReturn {
	const isIOS = Capacitor.getPlatform() === "ios";
	const callbacksRef = useRef(callbacks);
	const cleanupRef = useRef<(() => void) | null>(null);
	
	// Keep callbacks ref updated
	useEffect(() => {
		callbacksRef.current = callbacks;
	}, [callbacks]);

	// Setup and cleanup listeners
	useEffect(() => {
		if (!isIOS || !enabled) {
			// Cleanup if disabled or not on iOS
			if (cleanupRef.current) {
				cleanupRef.current();
				cleanupRef.current = null;
			}
			return;
		}

		const setupListeners = async (): Promise<void> => {
			try {
				// Dynamic import to avoid loading on non-iOS
				const { AudioSession } = await import("@capgo/capacitor-audio-session");
				
				console.log("[useAudioSession] Setting up iOS audio session listeners");

				// Handle interruption events (single event with type: 'began' | 'ended')
				const interruptionHandle = await AudioSession.addListener(
					"interruption",
					(type: InterruptionType) => {
						console.log(`[useAudioSession] Interruption: ${type}`);
						if (type === "began") {
							callbacksRef.current.onInterruptionBegan?.();
						} else if (type === "ended") {
							callbacksRef.current.onInterruptionEnded?.();
						}
					}
				);

				// Handle route change events
				const routeChangeHandle = await AudioSession.addListener(
					"routeChanged",
					(reason: RouteChangeReason) => {
						console.log("[useAudioSession] Route changed:", reason);
						
						// Notify about general route change
						callbacksRef.current.onRouteChange?.(reason);

						// Handle specific cases
						switch (reason) {
							case "old-device-unavailable":
								// Headphones/Bluetooth disconnected - typically should pause
								console.log("[useAudioSession] Device disconnected");
								callbacksRef.current.onHeadphonesDisconnected?.();
								break;

							case "new-device-available":
								// New device connected
								console.log("[useAudioSession] New device connected");
								callbacksRef.current.onNewDeviceConnected?.();
								break;

							case "override":
								// Route was overridden (e.g., speaker phone)
								console.log("[useAudioSession] Route override");
								break;

							case "category-change":
								// Audio category changed
								console.log("[useAudioSession] Category changed");
								break;

							default:
								break;
						}
					}
				);

				console.log("[useAudioSession] iOS audio session listeners configured");

				// Store cleanup function
				cleanupRef.current = (): void => {
					console.log("[useAudioSession] Cleaning up iOS audio session listeners");
					void interruptionHandle.remove();
					void routeChangeHandle.remove();
				};
			} catch (error) {
				console.error("[useAudioSession] Failed to setup listeners:", error);
			}
		};

		void setupListeners();

		return (): void => {
			if (cleanupRef.current) {
				cleanupRef.current();
				cleanupRef.current = null;
			}
		};
	}, [isIOS, enabled]);

	// Manual control for audio session active state
	// Note: @capgo/capacitor-audio-session doesn't expose activate/deactivate methods
	// This is kept for API compatibility but is currently a no-op
	const setActive = useCallback((active: boolean): Promise<void> => {
		if (!isIOS) {
			return Promise.resolve();
		}
		console.log(`[useAudioSession] setActive(${active}) called but plugin doesn't support manual activation`);
		return Promise.resolve();
	}, [isIOS]);

	return {
		isActive: isIOS && enabled,
		setActive,
	};
}

/**
 * Simplified hook that auto-pauses a media engine on interruptions.
 * Use this in conjunction with NativeMediaEngine.
 */
export function useAudioSessionWithEngine(
	enginePause: () => Promise<void>,
	enginePlay: () => Promise<void>,
	enabled: boolean = true
): UseAudioSessionReturn {
	const wasPlayingRef = useRef(false);

	const callbacks: AudioSessionCallbacks = {
		onInterruptionBegan: () => {
			// Store state and pause
			wasPlayingRef.current = true; // Assume was playing if we got here
			enginePause().catch(console.error);
		},
		onInterruptionEnded: () => {
			// Resume if we were playing before the interruption
			if (wasPlayingRef.current) {
				enginePlay().catch(console.error);
			}
			wasPlayingRef.current = false;
		},
		onHeadphonesDisconnected: () => {
			// Pause when headphones are disconnected
			enginePause().catch(console.error);
			wasPlayingRef.current = false;
		},
	};

	return useAudioSession(callbacks, enabled);
}
